import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Chip, Button, Checkbox } from '@mui/material';
import { MdRefresh } from 'react-icons/md';
import { TableSection, TableTitle, TableWrapper, Table } from './SectorOutlook.styles';
import { fetchWeeklyPicks, triggerWeeklyPicks, generateWeeklyPicks } from '../api/stocks';
import { addToWatchlist } from '../api/watchlist';
import { SymbolWithTradingView, symbolCellTdStyle } from '../components/TradingViewLink';
import { AI_PICKS_CACHE_KEY } from '../utils/livePageCacheKeys';
import { runLiveMarketPageMountPoll, runScreenPayloadFetch } from '../utils/screenPageLoader';
import { readPageCache } from '../utils/pageDataCache';

const applyPicksPayload = (resp, setData) => {
  setData({
    bullish: resp?.bullish ?? [],
    bearish: resp?.bearish ?? [],
    fno_bullish: resp?.fno_bullish ?? [],
    fno_bearish: resp?.fno_bearish ?? [],
    pick_date: resp?.pick_date ?? null,
  });
};

const gradeColor = { A: '#1b5e20', B: '#2e7d32', C: '#f57f17', D: '#c62828' };
const gradeBg = { A: '#e8f5e9', B: '#f1f8e9', C: '#fff8e1', D: '#ffebee' };
const compact = { fontSize: 11, padding: '4px 5px', whiteSpace: 'nowrap' };

function resolveRecoLabel(row, isBullish) {
  const raw = String(row?.recommendation || '').trim();
  const up = raw.toUpperCase();
  // Backend can emit HOLD/empty for some generated rows; keep page actionable.
  if (!up || up === 'HOLD' || up === 'NEUTRAL') return isBullish ? 'BUY' : 'SELL';
  if (up.includes('BUY')) return 'BUY';
  if (up.includes('SELL')) return 'SELL';
  return up;
}

function normalizePickTargets(row, isBullish) {
  const entry = Number(row?.entry_price);
  const sl = Number(row?.stop_loss);
  let t1 = Number(row?.target_1);
  let t2 = Number(row?.target_2);
  if (!Number.isFinite(entry) || !Number.isFinite(sl)) return row;
  if (!Number.isFinite(t1)) return row;
  if (!Number.isFinite(t2)) t2 = t1;
  const risk = Math.abs(entry - sl);
  const minGap = Math.max(risk * 1.5, entry * 0.02);
  if (isBullish && t2 <= t1) t2 = t1 + minGap;
  if (!isBullish && t2 >= t1) t2 = t1 - minGap;
  return { ...row, target_1: t1, target_2: t2 };
}

function computeRRTargets(row, isBullish) {
  const entry = Number(row?.entry_price);
  const sl = Number(row?.stop_loss);
  if (!Number.isFinite(entry) || !Number.isFinite(sl) || entry <= 0) {
    return { risk: null, t4r: null, t6r: null, t10r: null, t4r_pct: null, t6r_pct: null, t10r_pct: null };
  }
  const risk = Math.abs(entry - sl);
  if (!(risk > 0)) {
    return { risk: null, t4r: null, t6r: null, t10r: null, t4r_pct: null, t6r_pct: null, t10r_pct: null };
  }
  const sign = isBullish ? 1 : -1;
  return {
    risk,
    t4r: entry + sign * 4 * risk,
    t6r: entry + sign * 6 * risk,
    t10r: entry + sign * 10 * risk,
    t4r_pct: sign * 4 * risk / entry * 100,
    t6r_pct: sign * 6 * risk / entry * 100,
    t10r_pct: sign * 10 * risk / entry * 100,
  };
}

function AiPicksPage() {
  const [data, setData] = useState({ bullish: [], bearish: [], fno_bullish: [], fno_bearish: [], pick_date: null });
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState(null);
  const [added, setAdded] = useState({});
  const [checkedSymbols, setCheckedSymbols] = useState(new Set());
  const autoGenerateAttemptedRef = useRef(false);

  const load = useCallback(async ({ forceNetwork = false, silent = false } = {}) => {
    setError(null);
    return runScreenPayloadFetch({
      cacheKey: AI_PICKS_CACHE_KEY,
      fetcher: async () => {
        const resp = await fetchWeeklyPicks();
        return {
          bullish: resp?.bullish ?? [],
          bearish: resp?.bearish ?? [],
          fno_bullish: resp?.fno_bullish ?? [],
          fno_bearish: resp?.fno_bearish ?? [],
          pick_date: resp?.pick_date ?? null,
        };
      },
      applyPayload: (payload) => applyPicksPayload(payload, setData),
      setLoading,
      setError,
      forceNetwork,
      silent,
      hasUsable: (data) => Boolean(data?.bullish?.length || data?.bearish?.length),
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    let cleanup;
    const maybeAutoGenerate = async () => {
      if (cancelled || autoGenerateAttemptedRef.current) return;
      const cached = readPageCache(AI_PICKS_CACHE_KEY);
      const payload = cached?.data;
      const empty = !payload?.bullish?.length && !payload?.bearish?.length;
      if (!empty) return;
      autoGenerateAttemptedRef.current = true;
      setTriggering(true);
      try {
        await generateWeeklyPicks();
        if (cancelled) return;
        for (let attempt = 0; attempt < 12; attempt += 1) {
          await new Promise((r) => setTimeout(r, 5000));
          if (cancelled) return;
          try {
            const retry = await fetchWeeklyPicks();
            if (retry?.bullish?.length || retry?.bearish?.length) {
              setData({
                bullish: retry?.bullish ?? [],
                bearish: retry?.bearish ?? [],
                fno_bullish: retry?.fno_bullish ?? [],
                fno_bearish: retry?.fno_bearish ?? [],
                pick_date: retry?.pick_date ?? null,
              });
              setError(null);
              break;
            }
          } catch {
            /* keep polling */
          }
        }
      } catch (_) {
        /* ignore — user can refresh manually */
      } finally {
        if (!cancelled) setTriggering(false);
      }
    };
    const mountLoad = async (opts) => {
      await load(opts);
      if (!opts?.forceNetwork) {
        await maybeAutoGenerate();
      }
    };
    (async () => {
      await runLiveMarketPageMountPoll({
        load: mountLoad,
        onCleanup: (fn) => { cleanup = fn; },
      });
    })();
    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [load]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      await generateWeeklyPicks();
      for (let attempt = 0; attempt < 12; attempt += 1) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const retry = await fetchWeeklyPicks();
          if (retry?.bullish?.length || retry?.bearish?.length) {
            setData({
              bullish: retry?.bullish ?? [],
              bearish: retry?.bearish ?? [],
              fno_bullish: retry?.fno_bullish ?? [],
              fno_bearish: retry?.fno_bearish ?? [],
              pick_date: retry?.pick_date ?? null,
            });
            setError(null);
            return;
          }
        } catch {
          /* keep polling */
        }
      }
      await load();
    } catch (_) {
      try {
        await triggerWeeklyPicks();
      } catch {
        /* ignore */
      }
    } finally {
      setTriggering(false);
    }
  };

  const handleAdd = async (symbol, listType) => {
    const key = `${symbol}_${listType}`;
    if (added[key]) return;
    try {
      await addToWatchlist(symbol.toUpperCase(), listType, '');
      setAdded(prev => ({ ...prev, [key]: true }));
    } catch (_) { /* ignore */ }
  };

  const handleAddSelected = async (listType) => {
    const syms = [...checkedSymbols]
      .filter(Boolean)
      .filter((symbol) => !added[`${symbol}_${listType}`]);
    if (!syms.length) return;
    await Promise.allSettled(syms.map((symbol) => handleAdd(symbol, listType)));
  };

  const fmt = (v, decimals = 2) => {
    if (v == null) return '—';
    return `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  };

  const renderTable = (rows, type, titleOverride) => {
    const isBullish = type === 'bullish';
    const headerBg = isBullish ? '#1b5e20' : '#b71c1c';
    const title = titleOverride || (isBullish ? 'Bullish Swing Picks' : 'Bearish Swing Picks');

    return (
      <TableSection style={{ marginTop: 0 }}>
        <TableTitle style={{ color: headerBg, fontSize: 16 }}>{title}</TableTitle>
        <TableWrapper>
          <Table style={{ fontSize: 11, minWidth: 1120 }}>
            <thead style={{ backgroundColor: headerBg }}>
              <tr>
                <th style={compact}>#</th>
                <th style={compact}>Symbol</th>
                <th style={compact}>Grade</th>
                <th style={compact}>CMP</th>
                <th style={compact}>Entry</th>
                <th style={compact}>SL</th>
                <th style={compact}>SL%</th>
                <th style={compact}>T1</th>
                <th style={compact}>T2</th>
                <th style={{ ...compact, background: 'rgba(255,255,255,0.15)' }}>T 4R</th>
                <th style={{ ...compact, background: 'rgba(255,255,255,0.2)' }}>T 6R</th>
                <th style={{ ...compact, background: 'rgba(255,255,255,0.25)' }}>T 10R</th>
                <th style={compact}>Reco</th>
                <th style={compact}>
                  <Checkbox
                    size="small"
                    checked={rows.length > 0 && rows.every((r) => checkedSymbols.has(r.symbol))}
                    indeterminate={rows.some((r) => checkedSymbols.has(r.symbol)) && !rows.every((r) => checkedSymbols.has(r.symbol))}
                    onChange={() => {
                      const rowSyms = rows.map((r) => r.symbol).filter(Boolean);
                      const allSelected = rowSyms.length > 0 && rowSyms.every((s) => checkedSymbols.has(s));
                      setCheckedSymbols((prev) => {
                        const next = new Set(prev);
                        if (allSelected) rowSyms.forEach((s) => next.delete(s));
                        else rowSyms.forEach((s) => next.add(s));
                        return next;
                      });
                    }}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={14} style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                    No {type} picks available yet. Run the analysis to generate picks.
                  </td>
                </tr>
              )}
              {rows.map((r, i) => {
                const pick = normalizePickTargets(r, isBullish);
                const rr = computeRRTargets(pick, isBullish);
                const reco = resolveRecoLabel(r, isBullish);
                return (
                  <tr key={r.symbol}>
                    <td style={{ ...compact, fontWeight: 600, color: '#888' }}>{String(i + 1).padStart(2, '0')}</td>
                    <td style={symbolCellTdStyle({ ...compact, fontWeight: 700 }, 150)}>
                      <SymbolWithTradingView
                        symbol={r.symbol}
                        iconFirst
                        gap={6}
                        trailing={r.is_fno ? (
                          <span style={{
                            fontSize: 8, padding: '1px 3px', borderRadius: 2,
                            background: '#0d47a1', color: '#fff', fontWeight: 600,
                          }}>F&O</span>
                        ) : null}
                      >
                        {r.symbol}
                      </SymbolWithTradingView>
                    </td>
                    <td style={compact}>
                      <span style={{
                        display: 'inline-block', padding: '1px 8px', borderRadius: 3,
                        fontWeight: 700, fontSize: 11,
                        color: gradeColor[r.grade] || '#333',
                        background: gradeBg[r.grade] || '#f5f5f5',
                      }}>
                        {r.grade}
                      </span>
                    </td>
                    <td style={{ ...compact, fontWeight: 600 }}>{fmt(r.cmp)}</td>
                    <td style={{ ...compact, fontWeight: 600, color: '#1565c0' }}>{fmt(r.entry_price)}</td>
                    <td style={{ ...compact, color: '#c62828' }}>{fmt(r.stop_loss)}</td>
                    <td style={{ ...compact, color: (r.sl_pct && r.sl_pct < 3) ? '#2e7d32' : '#c62828', fontSize: 10 }}>
                      {r.sl_pct != null ? `${Number(r.sl_pct).toFixed(1)}%` : '—'}
                    </td>
                    <td style={{ ...compact, color: isBullish ? '#2e7d32' : '#c62828', fontWeight: 600 }}>{fmt(pick.target_1)}</td>
                    <td style={{ ...compact, color: isBullish ? '#2e7d32' : '#c62828', fontWeight: 600 }}>{fmt(pick.target_2)}</td>
                    <td style={{ ...compact, background: '#e3f2fd', fontWeight: 700, color: '#0d47a1' }}>
                      {rr.t4r != null ? fmt(rr.t4r) : '—'}
                      {rr.t4r_pct != null && <span style={{ fontSize: 9, marginLeft: 1, opacity: 0.7 }}>({rr.t4r_pct.toFixed(1)}%)</span>}
                    </td>
                    <td style={{ ...compact, background: '#e8eaf6', fontWeight: 700, color: '#283593' }}>
                      {rr.t6r != null ? fmt(rr.t6r) : '—'}
                      {rr.t6r_pct != null && <span style={{ fontSize: 9, marginLeft: 1, opacity: 0.7 }}>({rr.t6r_pct.toFixed(1)}%)</span>}
                    </td>
                    <td style={{ ...compact, background: '#ede7f6', fontWeight: 700, color: '#4a148c' }}>
                      {rr.t10r != null ? fmt(rr.t10r) : '—'}
                      {rr.t10r_pct != null && <span style={{ fontSize: 9, marginLeft: 1, opacity: 0.7 }}>({rr.t10r_pct.toFixed(1)}%)</span>}
                    </td>
                    <td style={compact}>
                      <Chip
                        label={reco}
                        size="small"
                        sx={{
                          fontWeight: 700, fontSize: 10, height: 20,
                          bgcolor: reco.includes('BUY') ? '#e8f5e9'
                            : reco.includes('SELL') ? '#ffebee' : '#f5f5f5',
                          color: reco.includes('BUY') ? '#1b5e20'
                            : reco.includes('SELL') ? '#b71c1c' : '#333',
                        }}
                      />
                    </td>
                    <td style={compact}>
                      <Checkbox
                        size="small"
                        checked={checkedSymbols.has(r.symbol)}
                        onChange={() => {
                          setCheckedSymbols((prev) => {
                            const next = new Set(prev);
                            if (next.has(r.symbol)) next.delete(r.symbol);
                            else next.add(r.symbol);
                            return next;
                          });
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      </TableSection>
    );
  };

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0b3d91' }}>
            Weekly AI Picks — Swing Trade Setup
          </h2>
          {data.pick_date ? (
            <span style={{ fontSize: 12, color: '#666' }}>Generated: {data.pick_date}</span>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button size="small" variant="contained" disabled={checkedSymbols.size === 0} onClick={() => handleAddSelected('short_term')} sx={{ textTransform: 'none' }}>
            {`Add ST (${checkedSymbols.size})`}
          </Button>
          <Button size="small" variant="contained" disabled={checkedSymbols.size === 0} onClick={() => handleAddSelected('long_term')} sx={{ textTransform: 'none', bgcolor: '#2e7d32' }}>
            {`Add LT (${checkedSymbols.size})`}
          </Button>
          <Tooltip title="Refresh picks">
            <IconButton onClick={load} size="small"><MdRefresh size={20} /></IconButton>
          </Tooltip>
          <Tooltip title="Generate picks now">
            <span>
              <IconButton onClick={handleTrigger} disabled={triggering} size="small"
                sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}>
                {triggering ? <CircularProgress size={18} /> : <MdRefresh size={20} color="#1565c0" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      )}

      {error && <div style={{ color: '#dc3545', fontWeight: 600, marginBottom: 16 }}>{error}</div>}

      {!loading && !error && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {renderTable(data.bullish, 'bullish')}
            {renderTable(data.bearish, 'bearish')}
          </Box>

          {(data.fno_bullish?.length > 0 || data.fno_bearish?.length > 0) && (
            <Box sx={{ mt: 4 }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, mb: 2,
                pb: 1, borderBottom: '2px solid #1565c0'
              }}>
                <Box sx={{
                  px: 1.5, py: 0.3, bgcolor: '#0d47a1', color: '#fff',
                  fontWeight: 800, fontSize: 13, borderRadius: 1, letterSpacing: 0.5
                }}>
                  F&O
                </Box>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0d47a1' }}>
                  Futures & Options — Swing Picks
                </h3>
                <span style={{ fontSize: 11, color: '#888' }}>
                  (Stocks available for derivatives trading on NSE)
                </span>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {renderTable(data.fno_bullish, 'bullish', 'F&O Bullish Picks')}
                {renderTable(data.fno_bearish, 'bearish', 'F&O Bearish Picks')}
              </Box>
            </Box>
          )}
        </>
      )}

      {!loading && !error && data.bullish.length === 0 && data.bearish.length === 0 && !triggering && (
        <Box sx={{ textAlign: 'center', py: 6, color: '#888', border: '2px dashed #e0e0e0', borderRadius: 2, mt: 2 }}>
          <p style={{ fontSize: 16, margin: 0 }}>No weekly picks available yet.</p>
          <p style={{ fontSize: 14, margin: '8px 0 0', color: '#aaa' }}>
            Picks are generated automatically every Saturday at 2:00 AM, or click the refresh button to trigger manually.
          </p>
        </Box>
      )}

      {triggering && !loading && (
        <Box sx={{ textAlign: 'center', py: 4, color: '#1565c0', border: '2px dashed #bbdefb', borderRadius: 2, mt: 2, bgcolor: '#e3f2fd' }}>
          <CircularProgress size={24} sx={{ mb: 1 }} />
          <p style={{ fontSize: 14, margin: '8px 0 0' }}>Generating AI picks from latest ratings data...</p>
        </Box>
      )}
    </div>
  );
}

export default AiPicksPage;
